import {
  dateFormatter,
  DateTimeValueSchemas,
  formatNumber,
} from "@validereinc/utilities";

const embeddedReportRowCommentTitleSeparator = " · ";

type EmbeddedReportRowCommentTitleCellType = {
  value: unknown;
};

type EmbeddedReportRowCommentTitleHeaderType = {
  position: number;
};

type EmbeddedReportRowCommentTitleArgsType = {
  rowIndexOnPage: number;
  page: number;
  pageSize: number;
  cells: Record<string, EmbeddedReportRowCommentTitleCellType | undefined>;
  uniqueColumnNames: readonly string[];
  headers: Record<string, EmbeddedReportRowCommentTitleHeaderType | undefined>;
};

const formatEmbeddedReportCellValueForCommentTitle = (
  value: unknown
): string => {
  if (value == null) return "";

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) return "";
    if (DateTimeValueSchemas.dateTime.safeParse(trimmedValue).success) {
      return dateFormatter(new Date(trimmedValue));
    }
    return trimmedValue;
  }

  if (typeof value === "number") {
    return formatNumber(value) ?? String(value);
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    return value
      .map((entry) => formatEmbeddedReportCellValueForCommentTitle(entry))
      .filter((entry) => entry.length > 0)
      .join(", ");
  }

  if (
    typeof value === "object" &&
    "name" in value &&
    typeof value.name === "string"
  ) {
    return value.name.trim();
  }

  return "";
};

const getOrderedUniqueColumnNames = (
  uniqueColumnNames: readonly string[],
  headers: EmbeddedReportRowCommentTitleArgsType["headers"]
) =>
  [...uniqueColumnNames].sort((columnNameA, columnNameB) => {
    const positionA =
      headers[columnNameA]?.position ?? Number.POSITIVE_INFINITY;
    const positionB =
      headers[columnNameB]?.position ?? Number.POSITIVE_INFINITY;
    return positionA - positionB;
  });

/** Row number from the table, then the values of the columns that uniquely identify the row. */
export const getEmbeddedReportRowCommentTitle = ({
  rowIndexOnPage,
  page,
  pageSize,
  cells,
  uniqueColumnNames,
  headers,
}: EmbeddedReportRowCommentTitleArgsType): string => {
  const rowNumber = (page - 1) * pageSize + rowIndexOnPage + 1;
  const uniqueValues = getOrderedUniqueColumnNames(uniqueColumnNames, headers)
    .map((columnName) =>
      formatEmbeddedReportCellValueForCommentTitle(cells[columnName]?.value)
    )
    .filter((value) => value.length > 0);

  if (uniqueValues.length === 0) {
    return String(rowNumber);
  }

  return [String(rowNumber), ...uniqueValues].join(
    embeddedReportRowCommentTitleSeparator
  );
};

/** The comments-panel row for `activeRowId` when that row is on the current page. */
export const getEmbeddedReportRowWithCommentsOnCurrentPage = <
  RowType extends { id: string },
>(
  rowsOnCurrentPage: readonly RowType[],
  activeRowId: string | undefined
): RowType | undefined => {
  if (!activeRowId) return undefined;

  return rowsOnCurrentPage.find((row) => row.id === activeRowId);
};

type EmbeddedReportRowForCommentTitleType = {
  row_id: string;
  cells: EmbeddedReportRowCommentTitleArgsType["cells"];
};

type ActiveEmbeddedReportRowCommentTitleArgsType = {
  reportRows: readonly EmbeddedReportRowForCommentTitleType[] | undefined;
  activeRowId: string | undefined;
  page: number;
  pageSize: number;
  uniqueColumnNames: readonly string[];
  headers: EmbeddedReportRowCommentTitleArgsType["headers"];
};

/** Title for the selected row when it is on the current page. */
export const getActiveEmbeddedReportRowCommentTitle = ({
  reportRows,
  activeRowId,
  page,
  pageSize,
  uniqueColumnNames,
  headers,
}: ActiveEmbeddedReportRowCommentTitleArgsType): string | undefined => {
  if (!reportRows || !activeRowId) return undefined;

  const rowIndexOnPage = reportRows.findIndex(
    (row) => row.row_id === activeRowId
  );
  if (rowIndexOnPage < 0) return undefined;

  return getEmbeddedReportRowCommentTitle({
    rowIndexOnPage,
    page,
    pageSize,
    cells: reportRows[rowIndexOnPage].cells,
    uniqueColumnNames,
    headers,
  });
};

export const EmbeddedReportRowCommentsPanelBody = {
  COMMENTS: "comments",
  EMPTY: "empty",
  ERROR: "error",
  PENDING: "pending",
} as const;

export type EmbeddedReportRowCommentsPanelBodyType =
  (typeof EmbeddedReportRowCommentsPanelBody)[keyof typeof EmbeddedReportRowCommentsPanelBody];

type EmbeddedReportRowCommentsPanelBodyArgsType = {
  hasLoadedComments: boolean;
  isCommentsRequestError: boolean;
  commentCount: number;
};

/** Missing comment data stays pending instead of an empty list. */
export const getEmbeddedReportRowCommentsPanelBody = ({
  hasLoadedComments,
  isCommentsRequestError,
  commentCount,
}: EmbeddedReportRowCommentsPanelBodyArgsType): EmbeddedReportRowCommentsPanelBodyType => {
  if (commentCount > 0) {
    return EmbeddedReportRowCommentsPanelBody.COMMENTS;
  }

  if (hasLoadedComments) {
    return EmbeddedReportRowCommentsPanelBody.EMPTY;
  }

  if (isCommentsRequestError) {
    return EmbeddedReportRowCommentsPanelBody.ERROR;
  }

  return EmbeddedReportRowCommentsPanelBody.PENDING;
};
